import { useEffect, useMemo } from 'react';

import { IItem, ILegacyCartEntry } from '@rbi-ctg/menu';
import { useCart } from 'hooks/use-cart';
import { createCartEntry } from 'utils/cart';

type useDonationProps = {
  getDonationPrice: (donationItem: IItem) => number;
  validDonationItemIds?: string[];
  isDonationOptionsLoaded?: boolean;
};

function useDonation({
  getDonationPrice,
  validDonationItemIds = [],
  isDonationOptionsLoaded = false,
}: useDonationProps) {
  const { cartEntries, addItemToCart, removeFromCart } = useCart();
  const donationCartEntries = useMemo(() => cartEntries.filter(cartEntry => cartEntry.isDonation), [
    cartEntries,
  ]);
  const validDonationItemIdSet = useMemo(() => new Set(validDonationItemIds), [
    validDonationItemIds,
  ]);
  const invalidDonationCartEntries = useMemo(() => {
    if (!isDonationOptionsLoaded) {
      return [];
    }

    return donationCartEntries.filter(cartEntry => !validDonationItemIdSet.has(cartEntry._id));
  }, [donationCartEntries, isDonationOptionsLoaded, validDonationItemIdSet]);
  const selectedDonationCartEntry = donationCartEntries.at(-1) ?? null;

  useEffect(() => {
    if (!isDonationOptionsLoaded || !invalidDonationCartEntries.length) {
      return;
    }

    invalidDonationCartEntries.forEach(cartEntry => {
      removeFromCart({ cartId: cartEntry.cartId });
    });
  }, [invalidDonationCartEntries, isDonationOptionsLoaded, removeFromCart]);

  function handleAddDonationCartEntry(donationItem: IItem) {
    if (selectedDonationCartEntry?._id === donationItem._id) {
      return;
    }

    donationCartEntries.forEach(cartEntry => {
      removeFromCart({ cartId: cartEntry.cartId });
    });

    const newDonationCartEntry: ILegacyCartEntry = {
      ...createCartEntry({
        item: donationItem,
        price: getDonationPrice(donationItem),
      }),
      isDonation: true,
    };
    addItemToCart(newDonationCartEntry);
  }

  function handleRemoveDonationCartEntry(_: { cartId: string }) {
    donationCartEntries.forEach(cartEntry => {
      removeFromCart({ cartId: cartEntry.cartId });
    });
  }

  return {
    selectedDonationCartEntry,
    isDonationEnabled: !!selectedDonationCartEntry,
    handleAddDonationCartEntry,
    handleRemoveDonationCartEntry,
  };
}

export default useDonation;
